import { LOBBY_STATUS } from 'asteroids-shared/protocol'
import { LOBBY_BELL_DURATION } from 'asteroids-shared/constants'
import { formatPlayerRowLabel } from './HUD.js'
import { replayerIcons } from '../replayer/InterfaceControls.js'

const MODE_MINUTES_TO_MS = { 1: 60000, 3: 180000, 5: 300000 }

export function shakeLobbyBell (root) {
  const btn = root.querySelector('[data-role="ring-bell"]')
  if (!(btn instanceof HTMLButtonElement)) return
  btn.removeAttribute('data-ringing')
  void btn.offsetWidth
  btn.setAttribute('data-ringing', '')
  const onEnd = () => {
    btn.removeAttribute('data-ringing')
    btn.removeEventListener('animationend', onEnd)
  }
  btn.addEventListener('animationend', onEnd)
}

export function renderLobby(root, state, actions) {
  const lobby = state.lobby
  const localClientId = state.myId
  const localPlayer = lobby.players.find(p => p.id === localClientId)
  const localVotes = localPlayer.votes
  const modeCoop = lobby.voteTallies.gameMode.coop ?? 0
  const modeDm = lobby.voteTallies.gameMode.deathmatch ?? 0
  const modeCtf = lobby.voteTallies.gameMode.ctf ?? 0
  const worldToroidal = lobby.voteTallies.worldMode.toroidal ?? 0
  const worldBouncy = lobby.voteTallies.worldMode.bouncy ?? 0
  const asteroidsOn = lobby.voteTallies.asteroidsMode.on ?? 0
  const shieldsOn = lobby.voteTallies.shieldsMode?.on ?? 0
  const hasModeVote = modeCoop + modeDm + modeCtf > 0
  const selectedMode = hasModeVote ? lobby.mode : null
  const asteroidsForcedOn = lobby.mode === 'coop'
  const asteroidsPressed = asteroidsForcedOn || localVotes.asteroidsMode === 'on'
  const shieldsPressed = localVotes.shieldsMode === 'on'
  const addOnVote = selectedMode === 'deathmatch' || selectedMode === 'ctf' ? 'gameDuration' : 'gameLives'
  const addOnLegend = addOnVote === 'gameLives' ? 'Lives' : 'Minutes'
  const addOnTally = n => (addOnVote === 'gameDuration'
    ? lobby.voteTallies.gameDuration[MODE_MINUTES_TO_MS[n]]
    : lobby.voteTallies.gameLives[n]) ?? 0
  const addOnExplicit = n => (addOnVote === 'gameDuration'
    ? localVotes.gameDuration === MODE_MINUTES_TO_MS[n]
    : localVotes.gameLives === n)
  const addOnSuggested = n => !addOnExplicit(n) && (addOnVote === 'gameDuration'
    ? localVotes.gameDuration == null && lobby.defaultDuration === MODE_MINUTES_TO_MS[n]
    : localVotes.gameLives == null && lobby.defaultLives === n)
  const toroidalSuggested = localVotes.worldMode == null && lobby.toroidalMap !== false
  const bouncySuggested = localVotes.worldMode == null && lobby.toroidalMap === false
  const coopSuggested = localVotes.gameMode == null && lobby.mode === 'coop'
  const showRingBell = lobby.players.length > 1
  const ringMs = LOBBY_BELL_DURATION

  root.innerHTML = `
      <section class="panel lobby-panel">
        <a class="scores-popover-trigger lobby-help-trigger" href="/rules.html" target="_blank" rel="noopener noreferrer">Game Mode Help</a>
        ${lobby.lastMatch ? '<button type="button" class="scores-popover-trigger" popovertarget="lobby-last-match">Previous Round Recap</button>' : ''}
        <p class="eyebrow">LOBBY</p>
        <h1>${lobby.roomName || 'Untitled Game'}</h1>
        ${lobby.isPrivate ? `
          <p>Private game code: <strong class="room-code-value">${lobby.roomCode}</strong></p>
          <p class="subtle">Share this code with friends. Game starts when everyone is ready.</p>
        ` : `
          <p class="subtle">Public game. Anyone on the main screen can join while the lobby is open.</p>
        `}
        <p class="subtle">
          ${lobby.toroidalMap === false
            ? 'Bouncy walls: ships and asteroids rebound off the border, and bullets are absorbed.'
            : 'Toroidal map: crossing an edge wraps you to the opposite side.'}
        </p>
        <div class="lobby-votes">
          <div class="lobby-votes-row">
            <fieldset class="lobby-vote">
              <legend class="subtle">World Options</legend>
              <button type="button" data-role="vote" data-vote="worldMode" data-value="toroidal"
                ${toroidalSuggested ? 'data-suggested' : ''}
                aria-pressed="${localVotes.worldMode === 'toroidal'}">${voteBtnLabel('Toroidal', worldToroidal)}</button>
              <button type="button" data-role="vote" data-vote="worldMode" data-value="bouncy"
                ${bouncySuggested ? 'data-suggested' : ''}
                aria-pressed="${localVotes.worldMode === 'bouncy'}">${voteBtnLabel('Bouncy Walls', worldBouncy)}</button>
              <button type="button" data-role="vote" data-vote="asteroidsMode" data-value="on"
                aria-pressed="${asteroidsPressed}"
                ${asteroidsForcedOn ? 'disabled' : ''}>${voteBtnLabel(asteroidsPressed ? 'Astroids On' : 'Astroids Off', asteroidsOn)}</button>
              <button type="button" data-role="vote" data-vote="shieldsMode" data-value="on"
                aria-pressed="${shieldsPressed}">${voteBtnLabel(shieldsPressed ? 'Shields On' : 'Shields Off', shieldsOn)}</button>
            </fieldset>
          </div>
          <div class="lobby-votes-row">
            <fieldset class="lobby-vote">
              <legend class="subtle">Game Mode</legend>
              <button type="button" data-role="vote" data-vote="gameMode" data-value="coop"
                ${coopSuggested ? 'data-suggested' : ''}
                aria-pressed="${localVotes.gameMode === 'coop'}">${voteBtnLabel('Co-op', modeCoop)}</button>
              <button type="button" data-role="vote" data-vote="gameMode" data-value="deathmatch" 
                aria-pressed="${localVotes.gameMode === 'deathmatch'}">${voteBtnLabel('Deathmatch', modeDm)}</button>
              <button type="button" data-role="vote" data-vote="gameMode" data-value="ctf"
                aria-pressed="${localVotes.gameMode === 'ctf'}">${voteBtnLabel('CTF', modeCtf)}</button>
            </fieldset>
            <fieldset class="lobby-vote add-on">
              <legend class="subtle">${addOnLegend}</legend>
              <button type="button" data-role="vote" data-vote="${addOnVote}" data-value="1" 
                ${addOnSuggested(1) ? 'data-suggested' : ''}
                aria-pressed="${addOnExplicit(1)}">${voteBtnLabel('1', addOnTally(1))}</button>
              <button type="button" data-role="vote" data-vote="${addOnVote}" data-value="3" 
                ${addOnSuggested(3) ? 'data-suggested' : ''}
                aria-pressed="${addOnExplicit(3)}">${voteBtnLabel('3', addOnTally(3))}</button>
              <button type="button" data-role="vote" data-vote="${addOnVote}" data-value="5" 
                ${addOnSuggested(5) ? 'data-suggested' : ''}
                aria-pressed="${addOnExplicit(5)}">${voteBtnLabel('5', addOnTally(5))}</button>
            </fieldset>
          </div>
        </div>
        ${lobby.startBlockReason === 'ctf_even_players_required' ? `
          <p class="lobby-warning">Capture the Flag needs an even number of players.</p>
        ` : ''}
        <ul class="lobby-players">
          ${lobby.players.map(player => {
            const isLocalRow = player.id === localClientId
            const localClientKickVote = player.kickVotesAgainst.includes(localClientId)
            const tally = player.kickVotesAgainst.length
            const need = Math.max(0, lobby.players.length - 1)
            return `
              <li class="player-row">
                <span class="name">
                  <span class="dot" style="--color-dot: ${player.color}"></span>
                  ${player.name}${player.id === lobby.hostId ? ' *' : ''}${isLocalRow ? ' (you)' : ''}
                  ${player.teamName ? `<span class="team-tag" data-team="${player.teamId}">${player.teamName}</span>` : ''}
                </span>
                <span class="status" data-status="${player.status === LOBBY_STATUS.READY ? 'ready' : 'waiting'}">
                  ${player.status === LOBBY_STATUS.READY ? 'READY' : 'waiting'}
                </span>
                ${!isLocalRow && lobby.players.length > 1 ? `
                  <button
                    type="button"
                    class="kick"
                    data-role="vote"
                    data-vote="kick"
                    data-player-id="${player.id}"
                    aria-pressed="${localClientKickVote}"
                    title="${tally}/${need} voted to kick"
                  >
                    ${localClientKickVote ? `Unkick (${tally}/${need})` : `Kick (${tally}/${need})`}
                  </button>
                ` : ''}
              </li>
            `
          }).join('')}
        </ul>
        <div class="lobby-actions">
          ${showRingBell ? `
          <button
            type="button"
            class="lobby-ring-bell"
            data-role="ring-bell"
            title="Ring bell"
            aria-label="Ring lobby bell"
            style="--lobby-ring-ms: ${ringMs}ms"
          >
            <svg viewBox="0 0 32 32" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-linecap="square" stroke-miterlimit="10" stroke-width="2"><path stroke-linecap="butt" d="M16 4v5"/><path d="M3 28h26M12 4h8M16 9A13 13 0 0 0 3 22v2h26v-2A13 13 0 0 0 16 9Z"/><path d="M16 13a9 9 0 0 0-7.5 4"/></g></svg>
          </button>` : ''}
          <button type="button" data-role="toggle-ready">
            ${localPlayer.status === LOBBY_STATUS.READY ? 'Not ready' : 'Ready'}
          </button>
          <button type="button" data-role="leave-room">Leave</button>
        </div>
        ${lobby.players.length > 0 && lobby.players.every(p => p.status === LOBBY_STATUS.READY) && lobby.canStart ? '<p class="lobby-starting">Starting...</p>' : ''}
        <div id="lobby-last-match" class="scores-popover" popover="auto">
          ${renderLastMatch(lobby, localClientId)}
        </div>
      </section>
  `

  root.querySelector('.lobby-panel').addEventListener('click', e => {
    const el = e.target.closest('[data-role]')
    if (!el) return
    switch (el.getAttribute('data-role')) {
      case 'vote':
        dispatchVoteAction(actions, el, localVotes)
        break
      case 'watch-replay':
        void actions.openReplay(el.dataset.replayId)
        break
      case 'toggle-ready':
        actions.toggleReady(localPlayer.status !== LOBBY_STATUS.READY)
        break
      case 'ring-bell': {
        actions.ringLobbyBell()
        shakeLobbyBell(root)
        break
      }
      case 'leave-room':
        actions.leaveRoom()
        break
    }
  })
}

function voteBtnLabel(text, count) {
  const n = Number(count)
  if (!n) return text
  return `${text}<span class="lobby-vote-tally" aria-hidden="true">${n}</span>`
}

function dispatchVoteAction(actions, el, localVotes) {
  const raw = el.dataset.value
  switch (el.dataset.vote) {
    case 'kick':
      actions.kickVote(el.dataset.playerId ?? '', el.getAttribute('aria-pressed') !== 'true')
      break
    case 'gameMode':
      actions.castVote({ gameMode: localVotes.gameMode === raw ? null : raw })
      break
    case 'worldMode':
      actions.castVote({ worldMode: localVotes.worldMode === raw ? null : raw })
      break
    case 'asteroidsMode':
      actions.castVote({ asteroidsMode: localVotes.asteroidsMode === 'on' ? null : 'on' })
      break
    case 'shieldsMode':
      actions.castVote({ shieldsMode: localVotes.shieldsMode === 'on' ? null : 'on' })
      break
    case 'gameDuration': {
      const ms = MODE_MINUTES_TO_MS[Number(raw)]
      if (ms == null) return
      actions.castVote({ gameDuration: localVotes.gameDuration === ms ? null : ms })
      break
    }
    case 'gameLives': {
      const n = Number(raw)
      actions.castVote({ gameLives: localVotes.gameLives === n ? null : n })
      break
    }
  }
}

const lastMatchReplayRow = match =>
  `<button type="button" class="replay-link" data-role="watch-replay" data-replay-id="${String(match.replayId)}" aria-label="Watch replay" title="Watch replay">${replayerIcons.watchReplay}</button>`

function renderLastMatch(lobby, localClientId) {
  const match = lobby.lastMatch
  if (!match) return '<p class="subtle">No match yet.</p>'
  if (match.mode === 'coop') {
    return `
      <p>Final score: ${match.finalScore ?? 0}</p>
      <p>Waves cleared: ${match.wavesCleared ?? 0}</p>
      ${lastMatchReplayRow(match)}
    `
  }
  if (match.mode === 'ctf') {
    return `
      <p class="subtle">CTF results</p>
      <p>Yellow ${match.teamScores?.yellow ?? 0} - Cyan ${match.teamScores?.cyan ?? 0}</p>
      <div class="standings-list">
        ${(match.standings ?? []).map((row, index) => `
          <div class="standings-row">
            <span>#${index + 1}</span>
            <span>${formatPlayerRowLabel(row, { roster: lobby.players, hostId: lobby.hostId, localPlayerId: localClientId })}</span>
            <span>${row.score}</span>
            <span>${row.kills}/${row.deaths}</span>
          </div>
        `).join('')}
      </div>
      ${lastMatchReplayRow(match)}
    `
  }
  return `
    <p class="subtle">Deathmatch results</p>
    <div class="standings-list">
      ${(match.standings ?? []).map((row, index) => `
        <div class="standings-row">
          <span>#${index + 1}</span>
          <span>${formatPlayerRowLabel(row, { roster: lobby.players, hostId: lobby.hostId, localPlayerId: localClientId })}</span>
          <span>${row.score}</span>
          <span>${row.kills}/${row.deaths}</span>
        </div>
      `).join('')}
    </div>
    ${lastMatchReplayRow(match)}
  `
}
